<script setup>
/* global axios */
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import SettingsLayout from '../settings/SettingsLayout.vue';
import BaseSettingsHeader from '../settings/components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import AuthenticatedMediaImage from '../publications/AuthenticatedMediaImage.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import {
  states,
  actions,
  errors,
  errorText,
  renderText,
  localTime,
} from './model';
const route = useRoute();
const endpoint = computed(
  () => `/api/v1/accounts/${route.params.accountId}/tg_pins`
);
const groups = ref([]);
const history = ref([]);
const historyCursor = ref(null);
const selected = ref([]);
const versions = ref({});
const loading = ref(true);
const busy = ref(false);
const execution = ref(false);
const denied = ref(false);
const error = ref('');
const notice = ref('');
const dirty = ref(false);
const uncertain = ref(false);
const body = ref('');
const buttonText = ref('');
const buttonUrl = ref('');
const file = ref(null);
const dragDepth = ref(0);
const bannerUrl = ref('');
const removeBanner = ref(false);
const scheduled = ref(false);
const date = ref('');
const time = ref('12:00');
const repeat = ref('once');
const weekdays = ref([1, 2, 3, 4, 5, 6, 7]);
const dialog = ref(null);
const pending = ref(null);
let poll;
let localBlob;
const html = computed(() => renderText(body.value));
const chosen = computed(() =>
  groups.value.filter(g => selected.value.includes(g.id))
);
const ready = computed(
  () =>
    !loading.value &&
    !busy.value &&
    selected.value.length > 0 &&
    !uncertain.value
);
const canAct = computed(
  () =>
    ready.value &&
    execution.value &&
    !dirty.value &&
    chosen.value.every(g => g.target && !g.target.active)
);
const hasCurrent = computed(() =>
  chosen.value.every(g => g.target?.current_message_id)
);
const today = () =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
function captureVersions() {
  versions.value = Object.fromEntries(
    groups.value
      .filter(g => g.target)
      .map(g => [String(g.id), g.target.lock_version])
  );
}
function markDirty() {
  dirty.value = true;
  notice.value = '';
}
function fill(group) {
  const target = group?.target;
  body.value = target?.body || '';
  buttonText.value = target?.button_text || '';
  buttonUrl.value = target?.button_url || '';
  if (localBlob) URL.revokeObjectURL(localBlob);
  localBlob = null;
  file.value = null;
  bannerUrl.value = target?.banner_url || '';
  removeBanner.value = false;
  scheduled.value = target?.schedule?.enabled === true;
  date.value = target?.schedule?.date || today();
  time.value = target?.schedule?.time || '12:00';
  repeat.value = target?.schedule?.repeat || 'once';
  weekdays.value = target?.schedule?.weekdays || [1, 2, 3, 4, 5, 6, 7];
  captureVersions();
  dirty.value = false;
}
async function load(initial = false) {
  try {
    const response = await axios({ method: 'get', url: endpoint.value });
    groups.value = response.data.groups;
    history.value = [
      ...response.data.history,
      ...history.value.filter(
        row => !response.data.history.some(fresh => fresh.id === row.id)
      ),
    ];
    if (history.value.length <= 50)
      historyCursor.value = response.data.next_cursor;
    execution.value = response.data.execution;
    error.value = '';
    denied.value = false;
    if (!initial && !dirty.value && !busy.value && !uncertain.value)
      fill(chosen.value[0]);
    if (initial) {
      selected.value = groups.value.filter(g => g.manageable).map(g => g.id);
      fill(chosen.value[0]);
    }
    if (uncertain.value) {
      uncertain.value = false;
      notice.value =
        'Данные обновлены. Проверьте результат перед повторным действием.';
      captureVersions();
    }
  } catch (e) {
    error.value = errorText(e);
    denied.value = e.response?.status === 403;
  } finally {
    loading.value = false;
  }
}
async function olderHistory() {
  if (!historyCursor.value || busy.value) return;
  busy.value = true;
  try {
    const response = await axios({
      method: 'get',
      url: `${endpoint.value}?before=${historyCursor.value}`,
    });
    history.value.push(...response.data.history);
    historyCursor.value = response.data.next_cursor;
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}
function selectAll() {
  selected.value = groups.value.filter(g => g.manageable).map(g => g.id);
  captureVersions();
}
function edit(group) {
  selected.value = [group.id];
  fill(group);
  notice.value = `Редактирование: ${group.title}`;
}
function selectionChanged() {
  if (!dirty.value && chosen.value.length === 1) fill(chosen.value[0]);
  else captureVersions();
}
function acceptBanner(files) {
  if (busy.value || !files?.length) return;
  const candidate = files[0];
  if (
    files.length !== 1 ||
    !['image/jpeg', 'image/png'].includes(candidate.type) ||
    candidate.size > 10 * 1024 * 1024
  ) {
    error.value = errors.invalid_banner;
    return;
  }
  if (localBlob) URL.revokeObjectURL(localBlob);
  file.value = candidate;
  localBlob = URL.createObjectURL(candidate);
  bannerUrl.value = localBlob;
  removeBanner.value = false;
  error.value = '';
  markDirty();
}
function chooseFile(event) {
  acceptBanner(event.target.files);
  event.target.value = '';
}
function dragBanner(event) {
  if (!Array.from(event.dataTransfer?.types || []).includes('Files')) return;
  event.preventDefault();
  if (!busy.value) dragDepth.value += 1;
}
function leaveBanner() {
  dragDepth.value = Math.max(0, dragDepth.value - 1);
}
function dropBanner(event) {
  event.preventDefault();
  dragDepth.value = 0;
  acceptBanner(event.dataTransfer?.files);
}
function clearBanner() {
  if (localBlob) URL.revokeObjectURL(localBlob);
  localBlob = null;
  file.value = null;
  bannerUrl.value = '';
  removeBanner.value = true;
  markDirty();
}
async function save() {
  if (!ready.value) return;
  busy.value = true;
  error.value = '';
  notice.value = '';
  const payload = {
    request_key: crypto.randomUUID(),
    group_ids: selected.value,
    versions: versions.value,
    body: body.value,
    body_html: html.value,
    button_text: buttonText.value,
    button_url: buttonUrl.value,
    remove_banner: removeBanner.value,
    schedule: {
      enabled: scheduled.value,
      date: date.value,
      time: time.value,
      repeat: repeat.value,
      weekdays: weekdays.value,
    },
  };
  const data = new FormData();
  data.append('payload', JSON.stringify(payload));
  if (file.value) data.append('banner', file.value);
  try {
    const response = await axios({
      method: 'post',
      url: `${endpoint.value}/save`,
      data,
    });
    await load();
    captureVersions();
    dirty.value = false;
    file.value = null;
    notice.value = `Сохранено для групп: ${response.data.saved}. ${scheduled.value ? 'Перепубликация назначена по местному времени каждой группы.' : 'Сейчас ничего не публиковалось.'}`;
  } catch (e) {
    error.value = errorText(e);
    uncertain.value = !e.response || e.response.status >= 500;
  } finally {
    busy.value = false;
  }
}
function ask(kind, ids = selected.value, runId = null) {
  pending.value = { kind, ids: [...ids], runId };
  dialog.value.open();
}
const confirmDescription = computed(() => {
  const kind = pending.value?.kind;
  return `${{ publish: 'Отправить НОВЫЙ пост ботом и закрепить его? Предыдущий наш закреп будет снят после подтверждения нового; старое сообщение останется.', update: 'Изменить текущие сообщения в Telegram без перепубликации?', unpin: 'Снять наш закреп, сохранив сообщение?', delete: 'Удалить текущий наш пост из Telegram и остановить его расписание?', pause: 'Остановить будущие перепубликации?', retry: 'Повторить только неуспешный этап операции?', resolve: 'Вы проверили Telegram и вручную устранили возможный лишний пост или закреп? Автоматического повтора не будет.' }[kind] || 'Подтвердить действие?'} Групп: ${pending.value?.ids.length || 0}.`;
});
async function confirm() {
  if (!pending.value || busy.value) return;
  busy.value = true;
  error.value = '';
  notice.value = '';
  const p = pending.value;
  try {
    await axios({
      method: 'post',
      url: `${endpoint.value}/action`,
      data: {
        payload: {
          request_key: crypto.randomUUID(),
          kind: p.kind,
          group_ids: p.ids,
          versions:
            p.kind === 'retry' || p.kind === 'resolve'
              ? Object.fromEntries(
                  groups.value
                    .filter(g => g.target)
                    .map(g => [String(g.id), g.target.lock_version])
                )
              : versions.value,
          run_id: p.runId,
          confirmed_checked: p.kind === 'resolve',
        },
      },
    });
    await load();
    captureVersions();
    notice.value =
      'Действие принято. Результат по каждой группе появится ниже.';
    dialog.value.close();
  } catch (e) {
    error.value = errorText(e);
    uncertain.value = !e.response || e.response.status >= 500;
  } finally {
    busy.value = false;
  }
}
onMounted(async () => {
  await load(true);
  poll = setInterval(() => {
    if (!busy.value && !uncertain.value) load();
  }, 10000);
});
onBeforeUnmount(() => {
  clearInterval(poll);
  if (localBlob) URL.revokeObjectURL(localBlob);
});
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    loading-message="Загружаем посты-закрепы…"
  >
    <template #header>
      <BaseSettingsHeader
        title="Пост закреп"
        description="Посты от бота с баннером и кнопкой. Создание, редактирование и перепубликация по местному времени групп."
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-6 min-w-0 pb-8">
        <Banner v-if="error" color="ruby" role="alert">{{ error }}</Banner>
        <Banner v-if="notice" color="teal" role="status">{{ notice }}</Banner>
        <div class="flex flex-wrap gap-2">
          <Button
            label="Обновить"
            variant="faded"
            color="slate"
            :disabled="busy"
            @click="load()"
          /><Button
            v-if="!denied"
            label="Выбрать все группы"
            variant="faded"
            :disabled="busy"
            @click="selectAll"
          />
        </div>
        <p v-if="denied" class="text-n-slate-12">
          Раздел доступен администратору.
        </p>
        <template v-else>
          <p v-if="!groups.length && !error" class="text-n-slate-11">
            Подключённых групп пока нет. Добавьте группу и настройте права бота
            в разделе «ТГ группы».
          </p>
          <div v-else class="border border-n-weak rounded-xl overflow-hidden">
            <article
              v-for="group in groups"
              :key="group.id"
              class="flex flex-wrap items-center gap-3 p-4 border-b border-n-weak last:border-0 min-w-0"
            >
              <input
                :id="`pin-group-${group.id}`"
                v-model="selected"
                type="checkbox"
                :value="group.id"
                :disabled="busy || !group.manageable"
                :aria-label="group.title"
                @change="selectionChanged"
              />
              <div class="flex-1 basis-48 min-w-0">
                <label
                  :for="`pin-group-${group.id}`"
                  class="text-sm font-medium text-n-slate-12 break-words"
                  >{{ group.title }}</label
                >
                <p class="text-xs text-n-slate-11 break-words">
                  {{ group.bot_name }} ·
                  {{ group.timezone || 'Часовой пояс не определён' }}
                </p>
              </div>
              <div class="text-xs text-n-slate-11">
                <p>
                  {{
                    group.manageable
                      ? states[
                          group.target?.active?.state ||
                            group.target?.pin_state ||
                            'draft'
                        ]
                      : 'Публикация недоступна'
                  }}
                </p>
                <p v-if="group.target?.next_at">
                  Следующая:
                  {{ localTime(group.target.next_at, group.timezone) }}
                </p>
              </div>
              <a
                v-if="group.target?.telegram_url"
                :href="group.target.telegram_url"
                target="_blank"
                rel="noopener noreferrer"
                class="text-sm text-n-blue-9"
                >Открыть в Telegram</a
              >
              <Button
                label="Редактировать"
                variant="faded"
                color="slate"
                :disabled="busy || !group.manageable"
                @click="edit(group)"
              />
            </article>
          </div>
          <div
            v-if="groups.length"
            class="grid grid-cols-1 xl:grid-cols-2 gap-6 min-w-0"
          >
            <form class="flex flex-col gap-4 min-w-0" @submit.prevent="save">
              <h2 class="text-base font-medium text-n-slate-12">
                Содержимое · выбрано групп: {{ selected.length }}
              </h2>
              <Editor
                v-model="body"
                class="pin-body-editor"
                label="Текст поста"
                placeholder="Что хотите рассказать участникам?"
                :max-length="16384"
                :enable-canned-responses="false"
                :enabled-menu-options="[
                  'strong',
                  'em',
                  'link',
                  'undo',
                  'redo',
                  'code',
                ]"
                :disabled="busy"
                @update:model-value="markDirty"
              />
              <p class="text-xs text-n-slate-11">
                С баннером — до 1024 символов, без баннера — до 4096.
                Форматирование сохраняется.
              </p>
              <Input
                v-model="buttonText"
                label="Название кнопки"
                placeholder="Например: Заказать размещение"
                :disabled="busy"
                @update:model-value="markDirty"
              />
              <Input
                v-model="buttonUrl"
                label="Ссылка кнопки"
                placeholder="https://t.me/..."
                :disabled="busy"
                @update:model-value="markDirty"
              />
              <div
                class="flex flex-col gap-2 min-w-0 rounded-lg border border-dashed p-4 transition-colors"
                :class="
                  dragDepth && !busy
                    ? 'border-n-brand bg-n-blue-2 ring-2 ring-n-brand'
                    : 'border-n-weak'
                "
                :aria-busy="busy"
                @dragenter="dragBanner"
                @dragover.prevent
                @dragleave="leaveBanner"
                @drop="dropBanner"
              >
                <label for="pin-banner" class="text-sm text-n-slate-12">
                  Баннер · JPG или PNG, до 10 МБ
                </label>
                <p class="text-sm text-n-slate-11 m-0" aria-live="polite">
                  {{
                    dragDepth && !busy
                      ? 'Отпустите картинку здесь'
                      : 'Перетащите картинку сюда или выберите файл'
                  }}
                </p>
                <input
                  id="pin-banner"
                  type="file"
                  accept="image/png,image/jpeg"
                  class="block w-full text-sm"
                  :disabled="busy"
                  @change="chooseFile"
                />
                <p
                  v-if="file"
                  class="text-xs text-n-slate-11 break-all m-0"
                  role="status"
                >
                  {{ file.name }} · Нажмите «Сохранить для выбранных групп»,
                  чтобы сохранить баннер.
                </p>
              </div>
              <Button
                v-if="bannerUrl"
                label="Убрать баннер из шаблона"
                variant="faded"
                color="slate"
                :disabled="busy"
                @click="clearBanner"
              />
              <fieldset
                class="border border-n-weak rounded-xl p-4 flex flex-col gap-4"
                :disabled="busy"
              >
                <legend class="text-sm font-medium text-n-slate-12 px-1">
                  Расписание перепубликации
                </legend>
                <label class="flex items-center gap-2 text-sm text-n-slate-12"
                  ><input
                    v-model="scheduled"
                    type="checkbox"
                    @change="markDirty"
                  />
                  Перепубликовывать по расписанию</label
                >
                <template v-if="scheduled">
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label class="text-sm text-n-slate-12"
                      >Начать с даты<input
                        v-model="date"
                        type="date"
                        class="block w-full rounded-lg border border-n-weak p-2 bg-n-alpha-black2"
                        @input="markDirty" /></label
                    ><label class="text-sm text-n-slate-12"
                      >Местное время группы<input
                        v-model="time"
                        type="time"
                        class="block w-full rounded-lg border border-n-weak p-2 bg-n-alpha-black2"
                        @input="markDirty"
                    /></label>
                  </div>
                  <label class="text-sm text-n-slate-12"
                    >Повторять<select
                      v-model="repeat"
                      class="block w-full rounded-lg border border-n-weak p-2 bg-n-alpha-black2"
                      @change="markDirty"
                    >
                      <option value="once">Однократно</option>
                      <option value="daily">Ежедневно</option>
                      <option value="weekly">По дням недели</option>
                    </select></label
                  >
                  <div v-if="repeat === 'weekly'" class="flex flex-wrap gap-3">
                    <label
                      v-for="(day, i) in [
                        'Пн',
                        'Вт',
                        'Ср',
                        'Чт',
                        'Пт',
                        'Сб',
                        'Вс',
                      ]"
                      :key="day"
                      class="flex gap-1 text-sm text-n-slate-12"
                      ><input
                        v-model="weekdays"
                        type="checkbox"
                        :value="i + 1"
                        @change="markDirty"
                      />{{ day }}</label
                    >
                  </div>
                  <p class="text-xs text-n-slate-11">
                    Например, 12:00 означает полдень в каждой выбранной группе.
                    После простоя просроченные посты не отправляются пачкой.
                  </p>
                </template>
              </fieldset>
              <Button
                label="Сохранить для выбранных групп"
                type="submit"
                :disabled="
                  !ready ||
                  !body.trim() ||
                  !buttonText.trim() ||
                  !buttonUrl.trim()
                "
                :is-loading="busy"
              />
              <p v-if="dirty" class="text-xs text-n-slate-11">
                Есть несохранённые изменения. Сначала сохраните их, затем
                публикуйте.
              </p>
            </form>
            <div class="flex flex-col gap-4 min-w-0">
              <h2 class="text-base font-medium text-n-slate-12">
                Предпросмотр
              </h2>
              <article
                class="border border-n-weak rounded-xl p-4 bg-n-alpha-black2 min-w-0 overflow-hidden"
              >
                <img
                  v-if="bannerUrl && localBlob"
                  :src="bannerUrl"
                  alt="Баннер поста"
                  class="w-full max-h-80 object-contain rounded-lg mb-3"
                />
                <AuthenticatedMediaImage
                  v-if="bannerUrl && !localBlob"
                  :src="bannerUrl"
                  alt="Баннер поста"
                  image-class="w-full max-h-80 object-contain rounded-lg mb-3"
                  placeholder-class="p-4 text-sm text-n-slate-11"
                />
                <!-- markdown disables raw HTML; DOMPurify sanitizes the preview. -->
                <div
                  v-if="body"
                  class="prose prose-sm max-w-none break-words text-n-slate-12"
                  v-html="html"
                />
                <p v-else class="text-sm text-n-slate-11">
                  Здесь появится ваш пост.
                </p>
                <div
                  class="border border-n-weak rounded-lg mt-4 p-3 text-center text-sm text-n-blue-9 break-words"
                >
                  {{ buttonText || 'Название кнопки' }}
                </div>
              </article>
              <div class="flex flex-wrap gap-2">
                <Button
                  label="Опубликовать и закрепить сейчас"
                  icon="i-lucide-pin"
                  :disabled="!canAct"
                  @click="ask('publish')"
                /><Button
                  label="Обновить текущий пост"
                  variant="faded"
                  :disabled="!canAct || !hasCurrent"
                  @click="ask('update')"
                /><Button
                  label="Снять закреп"
                  variant="faded"
                  color="slate"
                  :disabled="!canAct || !hasCurrent"
                  @click="ask('unpin')"
                /><Button
                  label="Удалить пост"
                  variant="faded"
                  color="ruby"
                  :disabled="!canAct || !hasCurrent"
                  @click="ask('delete')"
                /><Button
                  label="Остановить расписание"
                  variant="faded"
                  color="slate"
                  :disabled="
                    !ready || !execution || !chosen.every(g => g.target)
                  "
                  @click="ask('pause')"
                />
              </div>
              <p class="text-xs text-n-slate-11">
                Перепубликация создаёт новое сообщение. После его закрепления
                предыдущий наш закреп снимается; старое сообщение сохраняется.
                Ручные и чужие закрепы не меняются.
              </p>
              <Banner v-if="!execution" color="amber">
                Публикация временно недоступна. Черновики можно сохранять.
              </Banner>
            </div>
          </div>
          <section class="flex flex-col gap-3">
            <h2 class="text-base font-medium text-n-slate-12">
              Последние операции
            </h2>
            <p v-if="!history.length" class="text-sm text-n-slate-11">
              Публикаций ещё не было. После отправки здесь появятся результаты
              по каждой группе.
            </p>
            <article
              v-for="row in history"
              :key="row.id"
              class="border border-n-weak rounded-lg p-4 min-w-0"
            >
              <p class="text-sm text-n-slate-12 break-words">
                {{ groups.find(g => g.id === row.group_entry_id)?.title }} ·
                {{ actions[row.kind] }} · {{ states[row.state] }}
              </p>
              <p v-if="row.error_code" class="text-xs text-n-slate-11">
                {{
                  errors[row.error_code] ||
                  'Операция не выполнена. Проверьте права бота и сообщение в Telegram.'
                }}
              </p>
              <div class="flex flex-wrap gap-3 mt-2">
                <a
                  v-if="row.telegram_url"
                  :href="row.telegram_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-sm text-n-blue-9"
                  >Открыть сообщение</a
                ><Button
                  v-if="row.state === 'failed'"
                  label="Повторить неуспешный этап"
                  variant="faded"
                  :disabled="busy || !execution"
                  @click="ask('retry', [row.group_entry_id], row.id)"
                /><Button
                  v-if="row.state === 'unknown'"
                  label="Проверено в Telegram"
                  variant="faded"
                  color="slate"
                  :disabled="busy || !execution"
                  @click="ask('resolve', [row.group_entry_id], row.id)"
                />
              </div>
            </article>
            <Button
              v-if="historyCursor"
              label="Показать более ранние операции"
              variant="faded"
              color="slate"
              :disabled="busy"
              @click="olderHistory"
            />
          </section>
        </template>
      </div>
    </template>
  </SettingsLayout>
  <Dialog
    ref="dialog"
    title="Пост закреп"
    :description="confirmDescription"
    confirm-button-label="Подтвердить"
    cancel-button-label="Отмена"
    :is-loading="busy"
    @confirm="confirm"
  />
</template>

<style scoped>
.pin-body-editor :deep(.ProseMirror-woot-style) {
  height: 15rem;
  min-height: 7.5rem;
  max-height: none;
  resize: vertical;
  overflow: auto;
}
</style>
